import { Controller } from '@hotwired/stimulus';

/**
 * Concierge Calculator Controller
 *
 * Three range sliders (properties, bookings/day, fee/booking) driving an
 * estimated concierge-earnings figure on the Epic Trip feature page.
 *
 * Accessibility notes:
 * - Each slider gets `aria-valuetext` so a screen reader announces "$25 per
 *   booking" rather than a bare "25" (WCAG 4.1.2) — the raw value is meaningless
 *   on its own for the fee and bookings sliders.
 * - The visible figures update on every input event so they track the drag.
 *   Announcing those would queue one message per pixel moved, so the live region
 *   is a separate visually-hidden summary that updates on a debounce instead
 *   (WCAG 4.1.3). The visible figures deliberately carry no aria-live.
 *
 * Usage:
 *   <div data-controller="concierge-calculator">
 *     <input type="range" data-concierge-calculator-target="properties"
 *            data-action="input->concierge-calculator#recalculate">
 *     …
 *     <span data-concierge-calculator-target="perPropertyYear"></span>
 *     <p class="sr-only" aria-live="polite"
 *        data-concierge-calculator-target="summary"></p>
 *   </div>
 */
export default class extends Controller {
  static targets = [
    'properties',
    'bookings',
    'fee',
    'propertiesOut',
    'bookingsOut',
    'feeOut',
    'perPropertyYear',
    'perPropertyMonth',
    'portfolioYear',
    'summary',
  ];

  declare propertiesTarget: HTMLInputElement;
  declare bookingsTarget: HTMLInputElement;
  declare feeTarget: HTMLInputElement;
  declare propertiesOutTarget: HTMLElement;
  declare bookingsOutTarget: HTMLElement;
  declare feeOutTarget: HTMLElement;
  declare perPropertyYearTarget: HTMLElement;
  declare perPropertyMonthTarget: HTMLElement;
  declare portfolioYearTarget: HTMLElement;
  declare summaryTarget: HTMLElement;
  declare hasSummaryTarget: boolean;

  private announceTimer?: number;

  connect() {
    this.recalculate();
  }

  disconnect() {
    if (this.announceTimer) window.clearTimeout(this.announceTimer);
  }

  recalculate() {
    const properties = Number(this.propertiesTarget.value);
    const bookings = Number(this.bookingsTarget.value);
    const fee = Number(this.feeTarget.value);

    const perPropertyYear = bookings * 365 * fee;
    const portfolioYear = perPropertyYear * properties;

    // Visible read-outs track the drag frame by frame.
    this.propertiesOutTarget.textContent = String(properties);
    this.bookingsOutTarget.textContent = bookings.toFixed(1);
    this.feeOutTarget.textContent = this.usd(fee);
    this.perPropertyYearTarget.textContent = this.usd(perPropertyYear);
    this.perPropertyMonthTarget.textContent = `${this.usd(perPropertyYear / 12)} / mo in concierge fees`;
    this.portfolioYearTarget.textContent = this.usd(portfolioYear);

    // Spoken equivalents for the slider values themselves.
    this.propertiesTarget.setAttribute(
      'aria-valuetext',
      `${properties} ${properties === 1 ? 'property' : 'properties'}`,
    );
    this.bookingsTarget.setAttribute('aria-valuetext', `${bookings.toFixed(1)} bookings per day`);
    this.feeTarget.setAttribute('aria-valuetext', `${this.usd(fee)} per booking`);

    // One announcement per settled interaction, not per pixel.
    if (!this.hasSummaryTarget) return;
    if (this.announceTimer) window.clearTimeout(this.announceTimer);
    this.announceTimer = window.setTimeout(() => {
      this.summaryTarget.textContent = `Estimated ${this.usd(perPropertyYear)} per year per property, ${this.usd(portfolioYear)} per year across ${properties} ${properties === 1 ? 'property' : 'properties'}.`;
    }, 400);
  }

  private usd(value: number): string {
    return `$${Math.round(value).toLocaleString('en-US')}`;
  }
}
